import React, { useCallback, useEffect, useState } from 'react';
import { ChartOptions, TooltipItem } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { propertyInsightsClient } from 'clients';
import { HTTPError } from 'clients/HTTPClient';
import { SpotInterface } from 'types';
import { DriverDestination, DriverDestinationCategory } from 'types/PropertyInsights';
import doughnutGraph from 'assets/doughnutGraph.png';
import BriefcaseIcon from 'assets/icons/briefcase.svg?react';
import ShoppingBagIcon from 'assets/icons/bag.svg?react';
import RestaurantIcon from 'assets/icons/silverware.svg?react';
import EntertainmentIcon from 'assets/icons/mic.svg?react';
import MedicalIcon from 'assets/icons/hospital.svg?react';
import OtherIcon from 'assets/icons/run.svg?react';
import EmptyWidget from 'components/EmptyWidget/EmptyWidget';
import WidgetErrorMessage from 'components/WidgetErrorMessage/WidgetErrorMessage';
import WidgetBox from 'components/widgets/WidgetBox';
import { getDriverDestinationsGraphData } from '../helpers';
import { DriverDestinationsSkeleton } from './DriverDestinationsSkeleton';
import styles from './DriverDestinations.module.css';

type DriverDestinationsProps = {
    spot: SpotInterface;
};

const options: ChartOptions<'doughnut'> = {
    cutout: '60%',
    aspectRatio: 1,
    plugins: {
        tooltip: {
            callbacks: {
                label(tooltipItem: TooltipItem<'doughnut'>): string | string[] {
                    return `${tooltipItem.label}: ${tooltipItem.raw}%`;
                },
            },
        },
    },
};

export const driverDestinationCategoryIcon: Record<
    DriverDestinationCategory,
    { icon: React.ReactNode; background: string }
> = {
    [DriverDestinationCategory.OFFICE_WORK]: { icon: <BriefcaseIcon fill="#F35900" />, background: '#FFE9DD' },
    [DriverDestinationCategory.SHOPPING]: { icon: <ShoppingBagIcon fill="#74DAF8" />, background: '#E1F6FD' },
    [DriverDestinationCategory.RESTAURANT_DINING]: {
        icon: <RestaurantIcon fill="#008DC7" />,
        background: '#E3F5FF',
    },
    [DriverDestinationCategory.ENTERTAINMENT]: {
        icon: <EntertainmentIcon fill="#008677" />,
        background: '#E1F8F3',
    },
    [DriverDestinationCategory.MEDICAL]: { icon: <MedicalIcon fill="#334A00" />, background: '#E5FCC0' },
    [DriverDestinationCategory.OTHER]: { icon: <OtherIcon fill="#3E21A2" />, background: '#F1F1FF' },
};

function DriverDestinations({ spot }: DriverDestinationsProps): JSX.Element {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [graphData, setGraphData] = useState<{ data: number[]; labels: string[]; graphColors: string[] } | null>(
        null
    );
    const [report, setReport] = useState<DriverDestination[] | null>(null);

    const getDriverDestinations = useCallback(async () => {
        setLoading(true);
        setErrorMessage('');
        try {
            const response = await propertyInsightsClient.getDriverDestinations(spot.uuid);
            setReport(response);
            setGraphData(getDriverDestinationsGraphData(response));
        } catch (e) {
            if (HTTPError.isHTTPError(e)) {
                setErrorMessage(e.message);
            } else {
                setErrorMessage(e?.toString() || 'Something went wrong');
            }
        } finally {
            setLoading(false);
        }
    }, [spot.uuid]);

    useEffect(() => {
        getDriverDestinations();
    }, [spot.uuid, getDriverDestinations]);

    const isLoading = loading || !graphData || !report;
    const isEmpty = report && report.length === 0;

    return (
        <WidgetBox title="Driver Destinations">
            {errorMessage ? (
                <WidgetErrorMessage message={errorMessage} onRetry={getDriverDestinations} />
            ) : isLoading ? (
                <DriverDestinationsSkeleton />
            ) : isEmpty ? (
                <EmptyWidget imgUrl={doughnutGraph} />
            ) : (
                <div className={styles.driverDestinationsContainer}>
                    <Doughnut
                        data={{
                            labels: graphData.labels,
                            datasets: [
                                {
                                    data: graphData.data,
                                    backgroundColor: graphData.graphColors,
                                    borderWidth: 2,
                                    borderColor: '#fff',
                                },
                            ],
                        }}
                        options={options}
                        style={{ maxHeight: '260px' }}
                    />
                    <div className={styles.destinationItemsWrapper}>
                        <div className={styles.destinationItemsContainer}>
                            {report.map((destination) => {
                                const { icon, background } =
                                    driverDestinationCategoryIcon[destination.destination_category];
                                return (
                                    <div key={destination.destination_category} className={styles.destinationItem}>
                                        <div className={styles.destinationIcon} style={{ background }}>
                                            {icon}
                                        </div>
                                        <div className={styles.destinationCategoryContainer}>
                                            <p className={styles.destinationCategoryName}>
                                                {destination.destination_category}
                                            </p>
                                            <p
                                                className={styles.destinationTopDestinations}
                                                title={destination.top_destination_names}
                                            >
                                                {destination.top_destination_names}
                                            </p>
                                        </div>
                                        <p className={styles.destinationPercentage}>
                                            {destination.category_percentage}%
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </WidgetBox>
    );
}

export default DriverDestinations;
